import { useTranslation } from 'react-i18next';
import { forwardRef } from 'react';
import { AccordionHandle } from '@portal/ui-library';
import { formatGermanDate } from 'utils/dates';
import { SimpleTable } from '../SimpleTable/SimpleTable';
import { InsuredPersonDetailProps } from 'pages/DigitalOrderPage/models/insuredPersonDetailModels';
import { optionalValue, formatBoolean } from 'utils/formatters';
import { HealthQuestions } from '../HealthQuestions/HealthQuestions';
import { Typography } from '@mui/material';

export const InsuredPersonDetail = forwardRef<AccordionHandle, InsuredPersonDetailProps>(
  (props) => {
    const { t } = useTranslation('view360');
    return (
      <div className="grid grid-cols-1 gap-[24px]">
        <div className="col-span-1">
          <SimpleTable
            title={t('person.attributes.personalData')}
            rows={[
              {
                label: t(`person.attributes.salutation`),
                value: optionalValue(props.salutation)
              },
              {
                label: t(`person.attributes.birthDate`),
                value: optionalValue(props.birthDate, formatGermanDate)
              },
              {
                label: t(`person.attributes.academicTitle`),
                value: optionalValue(props.academicTitle)
              },
              {
                label: t(`person.attributes.firstName`),
                value: optionalValue(props.firstName)
              },
              { label: t(`person.attributes.lastName`), value: optionalValue(props.lastName) }
            ]}
          />
        </div>

        <div className="col-span-1">
          <SimpleTable
            title={t('person.attributes.companyData')}
            rows={[
              {
                label: t(`person.attributes.professionalPosition`),
                value: optionalValue(props.professionalPosition)
              },
              {
                label: t(`person.attributes.currentOccupation`),
                value: optionalValue(props.currentOccupation)
              }
            ]}
          />
        </div>

        <div className="col-span-1">
          <SimpleTable
            title={t('person.attributes.address')}
            rows={[
              {
                label: t(`person.attributes.isLivingInGermany`),
                value: optionalValue(props.isLivingInGermany, formatBoolean)
              },
              {
                label: t(`person.attributes.street`),
                value: optionalValue(props.street)
              },
              {
                label: t(`person.attributes.houseNumber`),
                value: optionalValue(props.houseNumber)
              },
              {
                label: t(`person.attributes.postalCode`),
                value: optionalValue(props.postalCode)
              },
              { label: t(`person.attributes.city`), value: optionalValue(props.city) },
              {
                label: t(`person.attributes.hasForeignResidence`),
                value: optionalValue(props.hasForeignResidence, formatBoolean)
              },
              { label: t(`person.attributes.country`), value: optionalValue(props.country) }
            ]}
          />
        </div>

        <div className="col-span-1" />
        <div className="col-span-1">
          {(props.stationaryHealthQuestions?.questions || []).length > 0 && (
            <div>
              <div className="row">
                <div className="col my-m">
                  <Typography
                    id="stationaryHealthQuestionsTitle"
                    variant="headlineXLGBold"
                    className="text-navigation-80"
                  >
                    {t('insuredPerson.health.stationaryHealthQuestionsTitle')}
                  </Typography>
                </div>
                <fieldset aria-labelledby="stationaryHealthQuestionsTitle">
                  <HealthQuestions {...props.stationaryHealthQuestions} />
                </fieldset>
              </div>
            </div>
          )}

          {(props.teethHealthQuestions?.questions || []).length > 0 && (
            <div>
              <div className="row">
                <div className="col my-m">
                  <Typography
                    id="teethHealthQuestionsTitle"
                    variant="headlineXLGBold"
                    className="text-navigation-80"
                  >
                    {t('insuredPerson.health.teethHealthQuestionsTitle')}
                  </Typography>
                </div>
                <fieldset aria-labelledby="teethHealthQuestionsTitle">
                  <HealthQuestions {...props.teethHealthQuestions} />
                </fieldset>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
